import { Box, styled } from '@mui/material'

export const importModalSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 2,
}

export const modalPanelSx = {
  width: 'min(560px, calc(100vw - 32px))',
  height: 'min(540px, calc(100vh - 32px))',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  borderRadius: '20px',
  backgroundColor: 'background.paper',
  boxShadow: '0 24px 48px rgba(24, 32, 44, 0.18)',
  outline: 'none',
}

export const modalHeaderSx = {
  flexShrink: 0,
  gap: 2,
  padding: '20px 24px 16px',
  borderBottom: '1px solid #EFEFEF',
}

export const stepperSx = {
  padding: 0,
  '& .MuiStep-root': { padding: 0 },
  '& .MuiStepConnector-root': { margin: '0 10px' },
  '& .MuiStepConnector-line': { borderColor: '#E1E4E8' },
  '& .MuiStepLabel-iconContainer': { paddingRight: 1 },
  '& .MuiStepLabel-label': {
    color: '#A5A9B3',
    fontSize: 12,
    fontWeight: 500,
  },
  '& .MuiStepLabel-label.Mui-active': {
    color: '#292929',
    fontWeight: 600,
  },
  '& .MuiStepLabel-label.Mui-completed': {
    color: '#292929',
    fontWeight: 500,
  },
}

export const modalBodySx = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: 3,
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
}

export const modalFooterSx = {
  minHeight: 72,
  flexShrink: 0,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 1,
  padding: '16px 24px',
  borderTop: '1px solid #EFEFEF',
  backgroundColor: 'background.paper',
}

export const cancelButtonSx = {
  minWidth: 80,
  height: 40,
  borderRadius: '8px',
  color: '#7A8096',
  fontSize: 12,
  fontWeight: 500,
  textTransform: 'none',
}

export const confirmButtonSx = {
  minWidth: 106,
  height: 40,
  gap: 1,
  borderRadius: '8px',
  backgroundColor: 'primary.main',
  color: 'common.white',
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: 'primary.dark' },
  '&.Mui-disabled': {
    backgroundColor: 'primary.main',
    color: 'common.white',
    opacity: 0.35,
  },
}

const getDropZoneColors = (selected: boolean, isDragging: boolean, hasError: boolean) => {
  if (hasError && !isDragging) {
    return { borderColor: 'error.main', backgroundColor: '#FEF2F2' }
  }
  if (selected || isDragging) {
    return { borderColor: 'primary.main', backgroundColor: '#F7FAFF' }
  }
  return { borderColor: '#DADDE3', backgroundColor: '#F8F9FA' }
}

export const dropZoneSx = (selected: boolean, isDragging = false, hasError = false) => ({
  minHeight: 176,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px dashed',
  ...getDropZoneColors(selected, isDragging, hasError),
  borderRadius: '8px',
  color: 'grey.700',
  cursor: 'pointer',
  transition: 'border-color 150ms ease, background-color 150ms ease',
  '&:hover': hasError
    ? {}
    : {
        borderColor: 'primary.main',
        backgroundColor: '#F7FAFF',
      },
})

const getDropZoneIconColor = (selected: boolean, hasError: boolean) => {
  if (hasError) {
    return 'error.main'
  }
  return selected ? 'primary.main' : '#7A8096'
}

export const dropZoneIconSx = (selected: boolean, hasError = false) => ({
  width: 40,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 0.5,
  border: '1px solid',
  borderColor: hasError ? '#FECACA' : '#DADDE3',
  borderRadius: '50%',
  backgroundColor: 'background.paper',
  color: getDropZoneIconColor(selected, hasError),
  fontSize: selected ? 16 : 15,
})

export const tableContainerSx = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  borderRadius: '8px',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  '& .MuiTableCell-head': { backgroundColor: '#F7F7F7' },
}

export const loadingProgressSx = {
  height: 4,
  borderRadius: 2,
  backgroundColor: '#E8EEFA',
  '& .MuiLinearProgress-bar': {
    borderRadius: 2,
    backgroundColor: 'primary.main',
  },
}

export const templateTooltipSx = {
  maxWidth: 'none',
  padding: 0,
  border: '1px solid #EFEFEF',
  borderRadius: '12px',
  backgroundColor: 'background.paper',
  color: '#292929',
  boxShadow: '0 12px 32px rgba(24, 32, 44, 0.16)',
}

export const templateTooltipArrowSx = {
  color: 'background.paper',
  '&::before': { border: '1px solid #EFEFEF' },
}

export const templateTableSx = {
  borderCollapse: 'collapse',
  fontSize: 11,
  '& th, & td': {
    padding: '6px 10px',
    border: '1px solid #E1E4E8',
    textAlign: 'left',
    whiteSpace: 'nowrap',
  },
  '& th': {
    backgroundColor: '#F7F7F7',
    color: '#292929',
    fontFamily: 'monospace',
    fontWeight: 600,
  },
}

export const templateDownloadLinkSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  color: 'primary.main',
  fontSize: 11,
  fontWeight: 600,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  '&:hover': { textDecoration: 'underline' },
}

export const TemplatePreviewTrigger = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  flexShrink: 0,
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.25, 0),
  color: theme.palette.primary.main,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.5,
  whiteSpace: 'nowrap',
  cursor: 'help',
  borderRadius: 4,
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}))
